(() => {
  'use strict';

  const STORAGE_KEY = 'ic:wishlist:v1';
  const UPDATED_KEY = 'ic:wishlist:updated:v1';
  const CHANGE_EVENT = 'ic:wishlist:changed';
  const PROXY_URL = '/apps/ic-wishlist';

  function readWishlist() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');

      return Array.isArray(parsed)
        ? parsed.filter(item => item && item.handle)
        : [];
    } catch (error) {
      console.warn('[IC Wishlist] Could not read wishlist.', error);
      return [];
    }
  }

  function writeWishlist(items, options = {}) {
    try {
      const safeItems =
        Array.isArray(items)
          ? items.filter(item => item && item.handle)
          : [];

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(safeItems)
      );

      if (!options.fromServer) {
        localStorage.setItem(
          UPDATED_KEY,
          new Date().toISOString()
        );
      }

      document.dispatchEvent(
        new CustomEvent(CHANGE_EVENT, {
          detail: {items: safeItems}
        })
      );

      if (!options.fromServer) {
        syncWishlistToServer(safeItems);
      }
    } catch (error) {
      console.warn(
        '[IC Wishlist] Could not save wishlist.',
        error
      );
    }
  }


  async function syncWishlistToServer(items) {
    try {
      const response = await fetch(PROXY_URL, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: Array.isArray(items)
            ? items
            : []
        })
      });

      if (response.status === 401) {
        return {
          loggedIn: false
        };
      }

      if (!response.ok) {
        throw new Error(
          `Wishlist sync failed: ${response.status}`
        );
      }

      const data =
        await response.json();

      if (data.updatedAt) {
        localStorage.setItem(
          UPDATED_KEY,
          data.updatedAt
        );
      }

      return data;
    } catch (error) {
      console.warn(
        '[IC Wishlist] Could not sync with customer account.',
        error
      );

      return null;
    }
  }


  async function hydrateWishlistFromServer() {
    try {
      const response = await fetch(PROXY_URL, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) {
        return;
      }

      const data =
        await response.json();

      if (
        !data.loggedIn ||
        !Array.isArray(data.items)
      ) {
        return;
      }

      const localItems =
        readWishlist();

      const localUpdatedAt =
        localStorage.getItem(UPDATED_KEY);

      const serverUpdatedAt =
        data.updatedAt || '';

      /*
       * If this browser has a genuinely newer local change,
       * push that change to Shopify.
       *
       * Otherwise Shopify is the source of truth. This lets
       * the wishlist follow a logged-in customer across devices.
       */
      if (
        localUpdatedAt &&
        serverUpdatedAt &&
        new Date(localUpdatedAt).getTime() >
          new Date(serverUpdatedAt).getTime()
      ) {
        await syncWishlistToServer(
          localItems
        );

        return;
      }

      writeWishlist(
        data.items,
        {fromServer: true}
      );

      if (serverUpdatedAt) {
        localStorage.setItem(
          UPDATED_KEY,
          serverUpdatedAt
        );
      }

      syncButtons();
      renderWishlistPage();
      renderWishlistDrawer();
      syncWishlistHeaderCount();
    } catch (error) {
      console.warn(
        '[IC Wishlist] Could not load customer wishlist.',
        error
      );
    }
  }

  function getProductFromButton(button) {
    return {
      id: button.dataset.productId || '',
      handle: button.dataset.productHandle || '',
      title: button.dataset.productTitle || '',
      url: button.dataset.productUrl || '',
      image: button.dataset.productImage || '',
      price: Number(button.dataset.productPrice || 0),
      priceFormatted: button.dataset.productPriceFormatted || '',
      currency:
        button.dataset.productCurrency ||
        (
          window.Shopify &&
          Shopify.currency
            ? Shopify.currency.active
            : ''
        ),
      vendor: button.dataset.productVendor || '',
      savedAt: new Date().toISOString()
    };
  }

  function isSaved(handle, items = readWishlist()) {
    return items.some(item => item.handle === handle);
  }

  function setButtonState(button, active) {
    if (!button) return;

    button.setAttribute(
      'aria-pressed',
      String(active)
    );

    const title =
      button.dataset.productTitle || 'item';

    button.setAttribute(
      'aria-label',
      `${active ? 'Remove' : 'Save'} ${title}${
        active ? ' from saved items' : ''
      }`
    );

    const label =
      button.querySelector('[data-ic-wishlist-label]');

    if (label) {
      label.textContent =
        active ? 'Saved' : 'Save';
    }
  }

  function syncButtons() {
    const items = readWishlist();

    document
      .querySelectorAll('[data-ic-wishlist-button]')
      .forEach(button => {
        setButtonState(
          button,
          isSaved(
            button.dataset.productHandle,
            items
          )
        );
      });
  }

  function pushAnalytics(action, product) {
    const eventName =
      action === 'add'
        ? 'add_to_wishlist'
        : 'remove_from_wishlist';

    window.dataLayer =
      window.dataLayer || [];

    window.dataLayer.push({
      event: eventName,
      ecommerce: {
        currency:
          product.currency || undefined,

        value:
          product.price
            ? product.price / 100
            : undefined,

        items: [
          {
            item_id:
              product.id || product.handle,

            item_name:
              product.title,

            item_brand:
              product.vendor ||
              'Ioana Ciolacu',

            price:
              product.price
                ? product.price / 100
                : undefined,

            quantity: 1
          }
        ]
      }
    });

    document.dispatchEvent(
      new CustomEvent(
        `ic:wishlist:${action}`,
        {
          detail: {product}
        }
      )
    );
  }

  let statusTimeout;

  function showStatus(message) {
    let status =
      document.querySelector(
        '.ic-wishlist-status'
      );

    if (!status) {
      status =
        document.createElement('div');

      status.className =
        'ic-wishlist-status';

      status.setAttribute(
        'role',
        'status'
      );

      status.setAttribute(
        'aria-live',
        'polite'
      );

      document.body.appendChild(status);
    }

    status.textContent = message;

    status.classList.add(
      'is-visible'
    );

    window.clearTimeout(
      statusTimeout
    );

    statusTimeout =
      window.setTimeout(
        () => {
          status.classList.remove(
            'is-visible'
          );
        },
        1400
      );
  }

  function toggle(button) {
    const product =
      getProductFromButton(button);

    if (!product.handle) {
      return;
    }

    let items =
      readWishlist();

    const index =
      items.findIndex(
        item =>
          item.handle ===
          product.handle
      );

    if (index >= 0) {
      const removed =
        items[index];

      items.splice(
        index,
        1
      );

      writeWishlist(items);

      pushAnalytics(
        'remove',
        removed
      );

      showStatus(
        'Removed from saved items'
      );
    } else {
      items.unshift(product);

      writeWishlist(items);

      pushAnalytics(
        'add',
        product
      );

      showStatus(
        'Saved for later'
      );
    }

    syncButtons();
    renderWishlistDrawer();
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function itemMarkup(item) {
    const title =
      escapeHtml(item.title);

    const url =
      escapeHtml(
        item.url ||
        `/products/${item.handle}`
      );

    const image =
      escapeHtml(item.image);

    const price =
      escapeHtml(
        item.priceFormatted
      );

    const handle =
      escapeHtml(item.handle);

    return `
      <article
        class="ic-wishlist-item"
        data-ic-wishlist-item="${handle}"
      >

        <a
          class="ic-wishlist-item__media-wrap"
          href="${url}"
        >
          ${
            image
              ? `
                <img
                  class="ic-wishlist-item__image"
                  src="${image}"
                  alt="${title}"
                  loading="lazy"
                >
              `
              : ''
          }
        </a>

        <button
          class="ic-wishlist-button ic-wishlist-item__remove"
          type="button"
          data-ic-wishlist-remove="${handle}"
          aria-label="Remove ${title} from saved items"
        >
          <span
            class="ic-wishlist-button__icon"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.75 3.75h10.5v16.5L12 16.8l-5.25 3.45V3.75Z"
                stroke="currentColor"
                stroke-width="1.25"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </button>

        <div
          class="ic-wishlist-item__info"
        >
          <a
            class="ic-wishlist-item__title"
            href="${url}"
          >
            ${title}
          </a>

          ${
            price
              ? `
                <span
                  class="ic-wishlist-item__price"
                >
                  ${price}
                </span>
              `
              : ''
          }

          <div class="ic-wishlist-item__actions">

            <a
              href="${url}"
              class="ic-wishlist-item__view"
            >
              View piece
            </a>

            <a
              href="${url}"
              class="ic-wishlist-item__buy"
            >
              Choose options
            </a>

            <button
              type="button"
              class="ic-wishlist-item__remove-text"
              data-ic-wishlist-remove="${handle}"
            >
              Remove
            </button>

          </div>
        </div>

      </article>
    `;
  }

  function renderWishlistPage() {
    const root =
      document.querySelector(
        '[data-ic-wishlist-page]'
      );

    if (!root) {
      return;
    }

    const grid =
      root.querySelector(
        '[data-ic-wishlist-grid]'
      );

    const empty =
      root.querySelector(
        '[data-ic-wishlist-empty]'
      );

    const count =
      root.querySelector(
        '[data-ic-wishlist-count]'
      );

    const items =
      readWishlist();

    document
      .querySelectorAll(
        '[data-ic-wishlist-header-count]'
      )
      .forEach(element => {
        element.textContent =
          String(items.length);
      });

    if (count) {
      count.textContent =
        `${items.length} ${
          items.length === 1
            ? 'item'
            : 'items'
        }`;
    }

    if (!items.length) {
      if (grid) {
        grid.innerHTML = '';
        grid.hidden = true;
      }

      if (empty) {
        empty.hidden = false;
      }

      return;
    }

    if (empty) {
      empty.hidden = true;
    }

    if (grid) {
      grid.hidden = false;

      grid.innerHTML =
        items
          .map(itemMarkup)
          .join('');
    }
  }

  function removeByHandle(handle) {
    const items =
      readWishlist();

    const item =
      items.find(
        entry =>
          entry.handle === handle
      );

    if (!item) {
      return;
    }

    writeWishlist(
      items.filter(
        entry =>
          entry.handle !== handle
      )
    );

    pushAnalytics(
      'remove',
      item
    );

    syncButtons();
    renderWishlistPage();
    renderWishlistDrawer();

    showStatus(
      'Removed from saved items'
    );
  }

  function getAccountSavedUrl() {
    const items = readWishlist();

    const handles =
      items
        .map(item => item.handle)
        .filter(Boolean);

    const base =
      'https://account.ioanaciolacu.com/pages/01a002a1-5398-7a1f-943b-115c20d78c26';

    if (!handles.length) {
      return base;
    }

    return `${base}?ic_sync=1&ic_saved=${encodeURIComponent(
      handles.join(',')
    )}`;
  }

  function openAccountSaved() {
    window.location.href =
      getAccountSavedUrl();
  }

  function drawerItemMarkup(item) {
    const title =
      escapeHtml(item.title);

    const url =
      escapeHtml(
        item.url ||
        `/products/${item.handle}`
      );

    const image =
      escapeHtml(item.image);

    const price =
      escapeHtml(
        item.priceFormatted
      );

    const handle =
      escapeHtml(item.handle);

    return `
      <article
        class="ic-saved-drawer__item"
        data-ic-saved-drawer-item="${handle}"
      >

        <a
          class="ic-saved-drawer__image-wrap"
          href="${url}"
        >
          ${
            image
              ? `
                <img
                  class="ic-saved-drawer__image"
                  src="${image}"
                  alt="${title}"
                  loading="lazy"
                >
              `
              : ''
          }
        </a>

        <div
          class="ic-saved-drawer__info"
        >

          <a
            href="${url}"
            class="ic-saved-drawer__product-title"
          >
            ${title}
          </a>

          ${
            price
              ? `
                <span
                  class="ic-saved-drawer__price"
                >
                  ${price}
                </span>
              `
              : ''
          }

          <div
            class="ic-saved-drawer__actions"
          >
            <a
              href="${url}"
              class="ic-saved-drawer__link"
            >
              View piece
            </a>

            <button
              type="button"
              class="ic-saved-drawer__remove"
              data-ic-saved-drawer-remove="${handle}"
            >
              Remove
            </button>
          </div>

        </div>

      </article>
    `;
  }

  function renderWishlistDrawer() {
    const drawer =
      document.getElementById(
        'ic-wishlist-drawer'
      );

    if (!drawer) {
      return;
    }

    const items =
      readWishlist();

    const container =
      drawer.querySelector(
        '[data-ic-saved-drawer-items]'
      );

    const empty =
      drawer.querySelector(
        '[data-ic-saved-drawer-empty]'
      );

    const footer =
      drawer.querySelector(
        '[data-ic-saved-drawer-footer]'
      );

    const count =
      drawer.querySelector(
        '[data-ic-saved-drawer-count]'
      );

    const accountLink =
      drawer.querySelector(
        '[data-ic-saved-account]'
      );

    if (accountLink) {
      accountLink.href =
        getAccountSavedUrl();
    }

    if (count) {
      count.textContent =
        String(items.length);
    }

    if (!items.length) {
      if (container) {
        container.innerHTML = '';
        container.hidden = true;
      }

      if (empty) {
        empty.hidden = false;
      }

      if (footer) {
        footer.hidden = true;
      }

      return;
    }

    if (empty) {
      empty.hidden = true;
    }

    if (footer) {
      footer.hidden = false;
    }

    if (container) {
      container.hidden = false;

      container.innerHTML =
        items
          .map(drawerItemMarkup)
          .join('');
    }
  }

  function openWishlistDrawer() {
    const drawer =
      document.getElementById(
        'ic-wishlist-drawer'
      );

    if (!drawer) {
      return;
    }

    renderWishlistDrawer();

    drawer.classList.add(
      'is-open'
    );

    drawer.setAttribute(
      'aria-hidden',
      'false'
    );

    document.body.classList.add(
      'ic-saved-drawer-open'
    );
  }

  function closeWishlistDrawer() {
    const drawer =
      document.getElementById(
        'ic-wishlist-drawer'
      );

    if (!drawer) {
      return;
    }

    drawer.classList.remove(
      'is-open'
    );

    drawer.setAttribute(
      'aria-hidden',
      'true'
    );

    document.body.classList.remove(
      'ic-saved-drawer-open'
    );
  }

  document.addEventListener(
    'click',
    event => {

      const headerLink =
        event.target.closest(
          '[data-ic-wishlist-header-link]'
        );

      if (headerLink) {
        event.preventDefault();

        openWishlistDrawer();

        return;
      }

      const close =
        event.target.closest(
          '[data-ic-saved-close]'
        );

      if (close) {
        event.preventDefault();

        closeWishlistDrawer();

        return;
      }

      const drawerRemove =
        event.target.closest(
          '[data-ic-saved-drawer-remove]'
        );

      if (drawerRemove) {
        event.preventDefault();

        removeByHandle(
          drawerRemove.dataset
            .icSavedDrawerRemove
        );

        return;
      }

      const button =
        event.target.closest(
          '[data-ic-wishlist-button]'
        );

      if (button) {
        event.preventDefault();
        event.stopPropagation();

        toggle(button);

        return;
      }

      const remove =
        event.target.closest(
          '[data-ic-wishlist-remove]'
        );

      if (remove) {
        event.preventDefault();

        removeByHandle(
          remove.dataset.icWishlistRemove
        );
      }
    }
  );

  document.addEventListener(
    'keydown',
    event => {
      if (event.key === 'Escape') {
        closeWishlistDrawer();
      }
    }
  );

  document.addEventListener(
    CHANGE_EVENT,
    () => {
      syncButtons();
      renderWishlistPage();
      renderWishlistDrawer();
      syncWishlistHeaderCount();
    }
  );

  document.addEventListener(
    'shopify:section:load',
    () => {
      syncButtons();
      renderWishlistPage();
      renderWishlistDrawer();
      syncWishlistHeaderCount();
    }
  );

  window.addEventListener(
    'storage',
    event => {
      if (
        event.key === STORAGE_KEY
      ) {
        syncButtons();
        renderWishlistPage();
        renderWishlistDrawer();
        syncWishlistHeaderCount();
      }
    }
  );

  window.ICWishlist = {
    getItems: readWishlist,

    replaceItems(items) {
      writeWishlist(
        Array.isArray(items)
          ? items
          : []
      );
    },

    contains(handle) {
      return isSaved(handle);
    },

    openAccountSaved,
    openDrawer: openWishlistDrawer,
    closeDrawer: closeWishlistDrawer,

    storageKey: STORAGE_KEY
  };

  function syncWishlistHeaderCount() {
    const items =
      readWishlist();

    document
      .querySelectorAll(
        '[data-ic-wishlist-header-count]'
      )
      .forEach(element => {
        element.textContent =
          String(items.length);
      });
  }

  function initWishlist() {
    syncButtons();
    renderWishlistPage();
    renderWishlistDrawer();
    syncWishlistHeaderCount();
    hydrateWishlistFromServer();
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initWishlist
    );
  } else {
    initWishlist();
  }

  window.addEventListener(
    'pageshow',
    syncWishlistHeaderCount
  );

})();